// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';

interface VendorSettings {
  email_notifications: boolean;
  auto_accept_orders: boolean;
  booking_buffer_hours: number;
  payment_terms: string;
}

interface VideoVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
}

const VendorSettings: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendorProfile, setVendorProfile] = useState<VideoVendorProfile | null>(null);
  const [settings, setSettings] = useState<VendorSettings>({
    email_notifications: true,
    auto_accept_orders: false,
    booking_buffer_hours: 24,
    payment_terms: '50% upfront, 50% on delivery',
  });

  useEffect(() => {
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;

    try {
      const { data: profileData, error: profileError } = await supabase
        .from('video_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profileError) throw profileError;
      setVendorProfile(profileData);

      // In a real app, you'd fetch actual settings from database
      // For now, using default values
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      // In a real app, you'd save these settings to a vendor_settings table
      // For now, just show success message
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Settings</h1>
        <p className="text-muted-foreground">Configure your vendor account preferences</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Notification Preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="email-notifications">Email Notifications</Label>
              <p className="text-sm text-muted-foreground">
                Receive email alerts for new orders and messages
              </p>
            </div>
            <Switch
              id="email-notifications"
              checked={settings.email_notifications}
              onCheckedChange={(checked) =>
                setSettings(prev => ({ ...prev, email_notifications: checked }))
              }
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Order Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="auto-accept">Auto-accept Orders</Label>
              <p className="text-sm text-muted-foreground">
                Automatically accept new orders without manual review
              </p>
            </div>
            <Switch
              id="auto-accept"
              checked={settings.auto_accept_orders}
              onCheckedChange={(checked) =>
                setSettings(prev => ({ ...prev, auto_accept_orders: checked }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="booking-buffer">Booking Buffer (hours)</Label>
            <Input
              id="booking-buffer"
              type="number"
              value={settings.booking_buffer_hours}
              onChange={(e) =>
                setSettings(prev => ({ ...prev, booking_buffer_hours: parseInt(e.target.value) || 0 }))
              }
              className="w-24"
            />
            <p className="text-sm text-muted-foreground">
              Minimum time between booking and event start
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="payment-terms">Payment Terms</Label>
            <Input
              id="payment-terms"
              value={settings.payment_terms}
              onChange={(e) =>
                setSettings(prev => ({ ...prev, payment_terms: e.target.value }))
              }
              placeholder="e.g., 50% upfront, 50% on delivery"
            />
            <p className="text-sm text-muted-foreground">
              Default payment terms shown to customers
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="flex items-center gap-2"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
};

export default VendorSettings;